import { i18n } from "@lingui/core";
import { Trans } from "@lingui/react";
import { isPrivateAdvertiser, validateEmail } from "@mobility-item-page/utilities";
import { ElementType, Intent } from "@mobility-item-page/utilities/client";
import { Box } from "@warp-ds/react/components/box";
import { Button } from "@warp-ds/react/components/button";
import { TextArea } from "@warp-ds/react/components/textarea";
import { TextField } from "@warp-ds/react/components/textfield";
import { Toggle } from "@warp-ds/react/components/toggle";
import { type Dispatch, type FormEvent, type SetStateAction, useEffect, useId, useState } from "react";
import { useAdViewMeta } from "../../context/AdViewProviderContext.tsx";
import { useAppConfig } from "../../context/AppConfigProviderContext.tsx";
import type { DetailsFeedback, FeedbackDevice } from "../../types/feedback.ts";
import usePulse from "../../utils/pulse-tracking/usePulse.ts";
import { Icon } from "./Icon.tsx";

enum FeedbackFormState {
  Initial = "initial",
  Hidden = "hidden",
  Feedback = "feedback",
  Submitting = "submitting",
  Submitted = "submitted",
  Error = "error",
}

const empty = (message: string) => message.trim().length === 0;

export const FeedbackForm = () => {
  const [hiddenInSession, setHiddenInSession] = useState(true);
  useEffect(() => {
    setHiddenInSession(window.sessionStorage.getItem("feedbackFormHidden") === "true");
  }, []);

  return hiddenInSession ? null : <FeedbackFormElement setHide={setHiddenInSession} />;
};

const FeedbackFormElement = ({ setHide }: { setHide: Dispatch<SetStateAction<boolean>> }) => {
  const [state, setState] = useState<FeedbackFormState>(FeedbackFormState.Initial);
  const [rating, setRating] = useState<"positive" | "negative" | undefined>(undefined);
  const [message, setMessage] = useState("");
  const [messageHelpText, setMessageHelpText] = useState("");
  const [charCount, setCharCount] = useState(0);
  const [canContact, setCanContact] = useState(false);
  const [email, setEmail] = useState("");
  const [emailHelpText, setEmailHelpText] = useState("");
  const formId = useId();
  const { adId, ownerUrn, ownerId } = useAdViewMeta();
  const { breadcrumbs, isMobile, helpCenter } = useAppConfig();
  const { trackClick } = usePulse();
  const maxChars = 500;

  const trackFeedbackClick = (intent: Intent) => {
    trackClick({
      name: "Feedback form",
      objectId: "MO_ev96",
      elementType: ElementType.Button,
      intent,
    });
  };

  const positiveTitle = i18n.t({
    id: "itemPageLayout.feedback.positiveTitle",
    message: "Fantastic! Anything we can improve on?",
  });
  const negativeTitle = i18n.t({
    id: "itemPageLayout.feedback.negativeTitle",
    message: "We're sorry to hear that. What was missing?",
  });
  const positivePlaceholder = i18n.t({
    id: "itemPageLayout.feedback.positivePlaceholder",
    message: "What could we do to make the experience better?",
  });
  const negativePlaceholder = i18n.t({
    id: "itemPageLayout.feedback.negativePlaceholder",
    message: "Please let us know what you were looking for...",
  });

  const validateMessage = (): boolean => {
    if (!message) {
      setMessageHelpText(
        i18n.t({
          id: "itemPageLayout.feedback.textArea.helpText.mandatory",
          message: "Must be filled out",
          comment: "Help text when field has changed and has no value, displayed underneath the text-area",
        }),
      );
      return false;
    }
    if (empty(message)) {
      setMessageHelpText(
        i18n.t({
          id: "dealer.contactForm.textArea.helpText.emptyText",
          message: "The message must contain text",
          comment:
            "Help text when the field has changed and is empty except spaces, displayed underneath the text-area",
        }),
      );
      setMessage("");
      return false;
    }
    setMessage(message.trim());
    setCharCount(message.trim().length);
    setMessageHelpText("");
    return true;
  };

  const validateEmailField = (): boolean => {
    if (!canContact) {
      return true;
    }
    if (empty(email)) {
      setEmailHelpText(
        i18n.t({
          id: "itemPageLayout.feedback.textArea.helpText.mandatory",
          message: "Must be filled out",
          comment: "Help text when field has changed and has no value, displayed underneath the text-area",
        }),
      );
      return false;
    }
    if (!validateEmail(email.trim())) {
      setEmailHelpText(
        i18n.t({
          id: "itemPageLayout.feedback.emailField.helpText.invalidEmail",
          message: "Please enter a valid email address",
          comment:
            "Help text when the email field has changed and contains an invalid email, displayed underneath the email field",
        }),
      );
      return false;
    }
    setEmail(email.trim());
    setEmailHelpText("");
    return true;
  };

  const handleSubmitDetails = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const isMessageValid = validateMessage();
    const isEmailValid = validateEmailField();
    if (!isMessageValid || !isEmailValid) {
      return;
    } else {
      setState(FeedbackFormState.Submitting);
    }
    const request: DetailsFeedback = {
      rating: rating ? rating : "undefined",
      adid: adId.toString(),
      l1: breadcrumbs[0].text,
      l2: breadcrumbs[1].text,
      l3: breadcrumbs[2]?.text || "",
      seller: isPrivateAdvertiser(ownerUrn) ? "private" : ownerId.toString(),
      device: (isMobile ? "Web Mobile" : "Web Desktop") as FeedbackDevice,
      text: message,
      contact: canContact,
      email: canContact ? email : undefined,
    };
    try {
      const response = await fetch("/mobility/api/feedback/details", {
        method: "POST",
        body: JSON.stringify(request),
        headers: {
          "Content-Type": "application/json",
        },
      });
      if (response.ok) {
        setState(FeedbackFormState.Submitted);
        trackFeedbackClick(Intent.Send);
      } else {
        throw new Error(`Server responded with status ${response.status}`);
      }
    } catch (_error) {
      // Handle network error
      setState(FeedbackFormState.Error);
    }
  };

  return (
    <Box bordered className="relative flex flex-col items-center mt-40 p-24">
      <Button
        utility
        quiet
        onClick={() => {
          setState(FeedbackFormState.Hidden);
          setHide(true);
          if (state === FeedbackFormState.Initial) {
            window.sessionStorage.setItem("feedbackFormHidden", "true");
          }
          trackFeedbackClick(Intent.Dismiss);
        }}
        className="absolute flex items-center top-4 right-4"
        aria-label="Close"
        disabled={state === FeedbackFormState.Submitting}
      >
        <Icon name="Close" />
      </Button>
      {state === FeedbackFormState.Initial && (
        <>
          <h2 className="t4 mb-16">
            <Trans id="itemPageLayout.feedback.title" message="Did you find what you were looking for?" />
          </h2>
          <div className="flex gap-8">
            <Button
              utility
              small
              className="flex items-center gap-8"
              onClick={() => {
                setState(FeedbackFormState.Feedback);
                setRating("positive");
                trackFeedbackClick(Intent.Like);
              }}
            >
              <Icon name="Like" />
              <Trans id="itemPageLayout.keyInfo.yes" message="Yes" />
            </Button>
            <Button
              utility
              small
              className="flex items-center gap-8"
              onClick={() => {
                setState(FeedbackFormState.Feedback);
                setRating("negative");
                trackFeedbackClick(Intent.Unlike);
              }}
            >
              <Icon name="Dislike" />
              <Trans id="itemPageLayout.keyInfo.no" message="No" />
            </Button>
          </div>
        </>
      )}
      {(state === FeedbackFormState.Feedback || state === FeedbackFormState.Submitting) && (
        <form id={formId} onSubmit={handleSubmitDetails} className="w-full flex flex-col items-center">
          <h2 className="t4 mb-16">
            <Trans id="itemPageLayout.feedback.title" message="Did you find what you were looking for?" />
          </h2>
          <TextArea
            minimumRows={5}
            maxLength={maxChars}
            className="w-full md:w-[60%]"
            label={rating === "positive" ? positiveTitle : negativeTitle}
            placeholder={rating === "positive" ? positivePlaceholder : negativePlaceholder}
            value={message}
            disabled={state === FeedbackFormState.Submitting}
            onChange={({ target: { value: newValue } }) => {
              if (messageHelpText) {
                setMessageHelpText("");
              }
              setCharCount(newValue.length);
              setMessage(newValue);
            }}
            onBlur={() => validateMessage()}
            helpText={
              messageHelpText ? (
                messageHelpText
              ) : (
                <Trans
                  id="itemPageLayout.feedback.helpText"
                  message="{charCount} characters left. If you need help, please contact our <link>customer service</link>"
                  values={{ charCount: maxChars - charCount }}
                  components={{
                    link: (
                      // biome-ignore lint/a11y/useAnchorContent: There is actual text the screen-reader can use here as seen in the message above
                      <a
                        href={helpCenter}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={i18n.t({
                          id: "itemPageLayout.feedback.helpCenterLink.ariaLabel",
                          message: "Open customer service in a new tab",
                        })}
                      />
                    ),
                  }}
                />
              )
            }
            invalid={!!messageHelpText}
          />
          <Box info className="flex flex-col items-start w-full md:w-[60%] mt-24 gap-24">
            <Trans
              id="itemPageLayout.feedback.notContactFormInfo"
              message="NOTE: This is not a contact form. To contact the seller, use the ‘Write to seller’ button."
            />
          </Box>
          <div className="flex flex-col items-start w-full md:w-[60%] mt-24 gap-24">
            <Toggle
              title={i18n.t({
                id: "itemPageLayout.feedback.contactUserText",
                message: "Can we contact you to find out more?",
              })}
              label={i18n.t({
                id: "itemPageLayout.feedback.contactUserLabel",
                message: "Yes, you are welcome to contact me.",
              })}
              type="checkbox"
              defaultChecked={canContact}
              onChange={(checked) => setCanContact(checked)}
              disabled={state === FeedbackFormState.Submitting}
            />
            {canContact && (
              <TextField
                label={i18n.t({ id: "itemPageLayout.signupAlert.emailLabel", message: "Email" })}
                value={email}
                name="email"
                className="w-full"
                disabled={state === FeedbackFormState.Submitting}
                onChange={(value) => {
                  if (emailHelpText) {
                    setEmailHelpText("");
                  }
                  setEmail(value.target.value);
                }}
                onBlur={() => {
                  setEmail((prevState) => prevState.trim());
                  validateEmailField();
                }}
                helpText={emailHelpText}
                invalid={!!emailHelpText}
              />
            )}
          </div>
          <div className="flex justify-center gap-8 w-full md:w-[60%] mt-24">
            <Button
              small
              type="button"
              disabled={state === FeedbackFormState.Submitting}
              onClick={() => {
                setState(FeedbackFormState.Submitted);
              }}
            >
              <Trans id="itemPageLayout.feedback.skipButton" message="Skip" />
            </Button>
            <Button small primary type="submit" form={formId} loading={state === FeedbackFormState.Submitting}>
              <Trans id="itemPageLayout.feedback.submitButton" message="Submit feedback" />
            </Button>
          </div>
        </form>
      )}
      {state === FeedbackFormState.Submitted && (
        <Trans id="itemPageLayout.feedback.success" message="Thank you for your feedback!" />
      )}
      {state === FeedbackFormState.Error && (
        <Trans id="itemPageLayout.feedback.error" message="Something went wrong, please try again later." />
      )}
    </Box>
  );
};
